import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import "moment/locale/pt-br";
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
} from "@material-ui/icons";
import {
  FormatDate,
  FormatRealNumber,
  NumberFormatCustom,
} from "../../../Components/Helper/FormatValues";
import {
  ShowSituation,
  ShowProjectStatus,
} from "../../../Components/ShowInfos";
import { CustomDatePicker } from "../../../Components/CustomDatePicker";
import { SelectCustom } from "../../../Components/SelectsCustom";
import api from "../../../services/Api";
import {
  GET_RQSTFORMINFO_TASK,
  GET_RQSTINFO_PROJECT,
  GET_STATUS_TASK,
} from "../../../services/options";
import { useSnackbar } from "notistack";
import { SelectApi } from "../../../Components/SelectsApi";

const EditMeasurementModal = ({ open, onClose, measurement, onSubmit }) => {
  const [editedMeasurement, setEditedMeasurement] = React.useState({
    executedDate: null,
    executedValue: "",
    status: null,
  });

  React.useEffect(() => {
    if (measurement) {
      setEditedMeasurement({
        executedDate:
          measurement.executedDate !== "Não definida"
            ? moment(measurement.executedDate, "DD/MM/YYYY").toDate()
            : null,
        executedValue: measurement.executedValue,
        status: {
          id: measurement.status?.id?.toString() || "0",
          name: measurement.status?.name || "Não iniciado",
        },
      });
    }
  }, [measurement]);

  const handleChange = (field) => (event) => {
    setEditedMeasurement((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (date) => {
    setEditedMeasurement((prev) => ({
      ...prev,
      executedDate: date,
    }));
  };

  const handleStatusChange = (event, value) => {
    setEditedMeasurement((prev) => ({
      ...prev,
      status: value ? { id: value.id, name: value.name } : null,
    }));
  };

  const handleSubmit = () => {
    // {id:9, executedDate: "99/99/9999", executedValue: 99, status: 9}
    const formattedMeasurement = {
      id: measurement.id,
      executedDate: moment(editedMeasurement.executedDate).format("DD/MM/YYYY"),
      executedValue:
        parseFloat(
          editedMeasurement.executedValue.replace(/\./g, "").replace(",", ".")
        ) || 0,
      status: parseInt(editedMeasurement.status?.id || "0"),
    };

    onSubmit(formattedMeasurement);
    onClose();
  };

  console.log("editedMeasurement", editedMeasurement);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Atualizar Medição
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          style={{ marginTop: "8px", gap: "12px" }}
        >
          <CustomDatePicker
            label="Data de execução"
            value={editedMeasurement.executedDate}
            onChange={handleDateChange}
            fullWidth
          />
          <TextField
            fullWidth
            label="Medição executada"
            variant="outlined"
            size="small"
            value={editedMeasurement.executedValue}
            onChange={handleChange("executedValue")}
            InputProps={{
              autoComplete: "off",
              inputComponent: NumberFormatCustom,
            }}
          />
          <SelectApi
            id="status"
            name="status"
            onChange={handleStatusChange}
            field={{
              value: editedMeasurement.status?.id?.toString() || "0",
            }}
            config={GET_STATUS_TASK()}
            textFieldProps={{
              fullWidth: true,
              size: "small",
              variant: "outlined",
              label: "Status",
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        style={{ padding: "16px", justifyContent: "space-between" }}
      >
        <Button
          variant="contained"
          style={{
            backgroundColor: "#000000",
            color: "white",
          }}
          onClick={onClose}
        >
          CANCELAR
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: "#4caf50",
            color: "white",
          }}
          onClick={handleSubmit}
        >
          CONFIRMAR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const NewMeasurementModal = ({
  open,
  onClose,
  onSubmit,
  projectStartDate,
  projectEndDate,
}) => {
  const [measurement, setMeasurement] = React.useState({
    name: "",
    startDate: null,
    endDate: null,
    expectedValue: "",
  });

  const [errors, setErrors] = React.useState({
    name: false,
    startDate: false,
    endDate: false,
    expectedValue: false,
    dateRange: false,
  });

  const handleChange = (field) => (event) => {
    setMeasurement((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: false,
      dateRange: false,
    }));
  };

  const handleDateChange = (field) => (date) => {
    setMeasurement((prev) => ({
      ...prev,
      [field]: date,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: false,
      dateRange: false,
    }));
  };

  const validateForm = () => {
    const newErrors = {
      name: !measurement.name,
      startDate: !measurement.startDate,
      endDate: !measurement.endDate,
      expectedValue: !measurement.expectedValue,
      dateRange: false,
    };

    if (measurement.startDate && measurement.endDate) {
      const startDate = moment(measurement.startDate).startOf("day");
      const endDate = moment(measurement.endDate).startOf("day");
      if (endDate.isBefore(startDate)) {
        newErrors.dateRange = true;
      }
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const formattedMeasurement = {
      ...measurement,
      startDate: moment(measurement.startDate).format("DD/MM/YYYY"),
      endDate: moment(measurement.endDate).format("DD/MM/YYYY"),
      expectedValue:
        parseFloat(
          measurement.expectedValue.replace(/\./g, "").replace(",", ".")
        ) || 0,
    };

    onSubmit(formattedMeasurement);
    setMeasurement({
      name: "",
      startDate: null,
      endDate: null,
      expectedValue: "",
    });
    setErrors({
      name: false,
      startDate: false,
      endDate: false,
      expectedValue: false,
      dateRange: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Nova Medição
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          gap={1}
          style={{ marginTop: "8px", gap: "12px" }}
        >
          <TextField
            fullWidth
            label="Medição"
            variant="outlined"
            size="small"
            value={measurement.name}
            onChange={handleChange("name")}
            required
            error={errors.name}
            helperText={errors.name ? "Campo obrigatório" : ""}
          />
          <Box display="flex" gap={1} style={{ gap: "12px" }}>
            <CustomDatePicker
              label="Data inicial"
              value={measurement.startDate}
              onChange={handleDateChange("startDate")}
              fullWidth
              required
              error={errors.startDate}
              helperText={errors.startDate ? "Campo obrigatório" : ""}
              minDate={
                projectStartDate
                  ? moment(projectStartDate, "YYYY-MM-DD").toDate()
                  : null
              }
              maxDate={
                projectEndDate
                  ? moment(projectEndDate, "YYYY-MM-DD").toDate()
                  : null
              }
            />
            <CustomDatePicker
              label="Data limite"
              value={measurement.endDate}
              onChange={handleDateChange("endDate")}
              fullWidth
              minDate={
                measurement.startDate ||
                (projectStartDate
                  ? moment(projectStartDate, "YYYY-MM-DD").toDate()
                  : null)
              }
              maxDate={
                projectEndDate
                  ? moment(projectEndDate, "YYYY-MM-DD").toDate()
                  : null
              }
              required
              error={errors.endDate || errors.dateRange}
              helperText={
                errors.endDate
                  ? "Campo obrigatório"
                  : errors.dateRange
                  ? "Data limite deve ser igual ou posterior à data inicial"
                  : ""
              }
            />
          </Box>
          <TextField
            fullWidth
            label="Valor previsto"
            variant="outlined"
            size="small"
            value={measurement.expectedValue}
            onChange={handleChange("expectedValue")}
            InputProps={{
              autoComplete: "off",
              inputComponent: NumberFormatCustom,
            }}
            required
            error={errors.expectedValue}
            helperText={errors.expectedValue ? "Campo obrigatório" : ""}
          />
        </Box>
      </DialogContent>
      <DialogActions
        style={{ padding: "16px", justifyContent: "space-between" }}
      >
        <Button
          variant="contained"
          style={{
            backgroundColor: "#000000",
            color: "white",
          }}
          onClick={onClose}
        >
          CANCELAR
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: "#4caf50",
            color: "white",
          }}
          onClick={handleSubmit}
        >
          CONFIRMAR
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TaskMeasurements = () => {
  const { taskId, id: projectId } = useParams();
  const [measurements, setMeasurements] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [isNewMeasurementOpen, setIsNewMeasurementOpen] = React.useState(false);
  const [editMeasurement, setEditMeasurement] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [taskInfo, setTaskInfo] = React.useState(null);
  const [projectInfo, setProjectInfo] = React.useState(null);
  const [disableNewMeasurement, setDisableNewMeasurement] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();
  console.log("taskId", taskId);
  console.log("projectId", projectId);

  const navigate = useNavigate();

  async function getTaskInfo() {
    await api(GET_RQSTFORMINFO_TASK(taskId))
      .then((r) => {
        setTaskInfo(r.data[0]);
        console.log("r.data[0]", r.data[0]);
        setDisableNewMeasurement(r.data[0].status.id == '2' || r.data[0].status.id == '3' || r.data[0].status.id == '4' || r.data[0].status.id == '5');
      })
      .catch((e) => {
        console.error("Error fetching task info:", e);
      });
  }

  async function getProjectInfo() {
    await api(GET_RQSTINFO_PROJECT(projectId))
      .then((r) => {
        setProjectInfo(r.data);
      })
      .catch((e) => {
        console.error("Error fetching project info:", e);
      });
  }

  const fetchMeasurements = async () => {
    try {
      const response = await api.get(`measurement/list.php?id=${taskId}`);
      if (response.data && Array.isArray(response.data)) {
        setMeasurements(
          response.data.map((measurement) => ({
            ...measurement,
            status: {
              id: measurement.status?.id?.toString() || "0",
              name: measurement.status?.name || "Não iniciado",
            },
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching measurements:", error);
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([getTaskInfo(), getProjectInfo(), fetchMeasurements()]);
      setIsLoading(false);
    };
    fetchData();
  }, [taskId]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = async (id) => {
    if (disableNewMeasurement) return;
    try {
      const response = await api.post(`measurement/delete.php?id=${id}`);
      console.log("response", response);

      if (response.data.status === "success") {
        await fetchMeasurements();
      }
    } catch (error) {
      console.error("Error deleting measurement:", error);
    }
  };

  const handleEdit = (measurement) => {
    if (disableNewMeasurement) return;
    setEditMeasurement(measurement);
  };

  const handleUpdateMeasurement = async (updatedMeasurement) => {
    if (disableNewMeasurement) return;
    try {
      const response = await api.post("measurement/alter.php", {
        id: updatedMeasurement.id,
        executedDate: updatedMeasurement.executedDate
          .split("/")
          .reverse()
          .join("/"),
        executedValue: updatedMeasurement.executedValue,
        status: updatedMeasurement.status,
      });

      if (response.data.status === "success") {
        await fetchMeasurements();
        setEditMeasurement(null);
      }
    } catch (error) {
      console.error("Error updating measurement:", error);
    }
  };

  const handleNewMeasurement = async (measurementData) => {
    if (disableNewMeasurement) return;
    try {
      const response = await api.post("measurement/insert.php", {
        idStep: taskId,
        name: measurementData.name,
        startDate: measurementData.startDate.split("/").reverse().join("/"),
        endDate: measurementData.endDate.split("/").reverse().join("/"),
        expectedValue: measurementData.expectedValue,
      });

      console.log("response", response);

      if (response.data.status === "success") {
        await fetchMeasurements();
        setIsNewMeasurementOpen(false);
      } else if (response.data.status === "caution") {
        enqueueSnackbar(response.data.msg, { variant: "warning" });
      } else if (response.data.status === "error") {
        enqueueSnackbar(response.data.msg, { variant: "error" });
      }
    } catch (error) {
      console.error("Error adding new measurement:", error);
    }
  };

  const filteredMeasurements = measurements.filter((measurement) =>
    measurement.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" style={{ marginBottom: "20px" }}>
          {projectInfo?.name} - {taskInfo?.name}{" "}
          {disableNewMeasurement && "(Visualização)"}
        </Typography>

        <div
          onClick={() => navigate(-1)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <ArrowBackIcon />
          Voltar
        </div>
      </div>

      <Paper style={{ padding: "16px", marginBottom: "20px" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ width: "300px" }}>
            <TextField
              fullWidth
              placeholder="Pesquisar medição..."
              value={searchTerm}
              onChange={handleSearch}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: <SearchIcon color="action" />,
              }}
            />
          </Box>
          <Box display="flex" gap={2}>
            {!disableNewMeasurement && (
              <>
                <Button
                  variant="contained"
                  style={{
                    backgroundColor: disableNewMeasurement ? "#999999" : "#2196f3",
                    color: "white",
                    cursor: disableNewMeasurement ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (disableNewMeasurement) return;
                    setIsNewMeasurementOpen(true);
                  }}
                  disabled={disableNewMeasurement}
                >
                  NOVA MEDIÇÃO
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Medição</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Situação</TableCell>
                <TableCell>Valor Previsto</TableCell>
                <TableCell>Valor Executado</TableCell>
                <TableCell>Data inicial</TableCell>
                <TableCell>Data prevista</TableCell>
                <TableCell>Data de execução</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMeasurements.map((measurement) => (
                <TableRow key={measurement.id}>
                  <TableCell>{measurement.name}</TableCell>
                  <TableCell>
                    {console.log(measurement?.status)}
                    <ShowProjectStatus
                      type={parseInt(measurement?.status?.id ?? "-1")}
                      value={0}
                    />
                  </TableCell>
                  <TableCell>
                    <ShowSituation value={measurement.situation} />
                  </TableCell>
                  <TableCell>
                    {FormatRealNumber(measurement.expectedValue)}
                  </TableCell>
                  <TableCell>
                    {FormatRealNumber(measurement.executedValue)}
                  </TableCell>
                  <TableCell>
                    {measurement.startDate === "0000-00-00"
                      ? "Não definida"
                      : FormatDate(measurement.startDate)}
                  </TableCell>
                  <TableCell>
                    {measurement.endDate === "0000-00-00"
                      ? "Não definida"
                      : FormatDate(measurement.endDate)}
                  </TableCell>
                  <TableCell>
                    {measurement.executedDate === "0000-00-00"
                      ? "Não definida"
                      : FormatDate(measurement.executedDate)}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      display="flex"
                      flexWrap="nowrap"
                      justifyContent="flex-end"
                      minWidth="120px"
                    >
                      {!disableNewMeasurement && (
                        <>
                          <IconButton
                            size="small"
                            style={{
                              backgroundColor: "#4caf50",
                              color: "white",
                              padding: "4px",
                              marginRight: "4px",
                            }}
                            onClick={() => handleEdit(measurement)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            style={{
                              backgroundColor: "#f44336",
                              color: "white",
                              padding: "4px",
                            }}
                            onClick={() => handleDelete(measurement.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <NewMeasurementModal
        open={isNewMeasurementOpen}
        onClose={() => setIsNewMeasurementOpen(false)}
        onSubmit={handleNewMeasurement}
        projectStartDate={taskInfo?.initDate}
        projectEndDate={taskInfo?.deadLine}
      />

      <EditMeasurementModal
        open={editMeasurement !== null}
        onClose={() => setEditMeasurement(null)}
        measurement={editMeasurement}
        onSubmit={handleUpdateMeasurement}
      />
    </div>
  );
};

export default TaskMeasurements;
